import React, { useState, useEffect } from 'react';
import { formatInterestDate } from '../../data/workshop2026InterestSurvey';
import { getWorkshopTabPhase } from '../../data/workshopPhase';
import { Meeting, MeetingMemberPreferences, MeetingMemberStatus, meetingService } from '../../services/meetingService';
import WorkshopPreferencesPanel from './WorkshopPreferencesPanel';
import WorkshopZoomBlock from './WorkshopZoomBlock';
import WorkshopCollapsibleCard from './WorkshopCollapsibleCard';
import WorkshopPhoneListSection from './WorkshopPhoneListSection';
import WorkshopCommunitySection from './WorkshopCommunitySection';
import WorkshopShareInviteBlock from './WorkshopShareInviteBlock';
import WorkshopStepWorkSection from './WorkshopStepWorkSection';
import WorkshopLeaveSection from './WorkshopLeaveSection';
import WorkshopCalendarMemberView from './WorkshopCalendarMemberView';
import StepWorkDiscoveryBanner from '../StepWorkDiscoveryBanner';
import styles from '../../styles/workshop2026Page.module.css';

interface Workshop2026WorkshopTabProps {
  registrationState: 'none' | 'interest' | 'confirmed';
  meeting: Meeting | null;
  meetingCode: string | null;
  memberStatus: MeetingMemberStatus | null;
  interestAt?: string | null;
  memberPreferences: MeetingMemberPreferences;
  profilePhone?: string;
  user?: unknown;
  userEmail?: string;
  workshopEngagedAt?: string | null;
  onJoinInterestList: () => void;
  onConfirmAttendance?: () => void;
  onLeaveWorkshop?: () => void;
  onSavePreferences: (updates: Partial<MeetingMemberPreferences>) => Promise<void>;
  onSavePhone: (phone: string) => Promise<void>;
  confirming?: boolean;
  leaving?: boolean;
  meetingCodeMissing: boolean;
}

const Workshop2026WorkshopTab: React.FC<Workshop2026WorkshopTabProps> = ({
  registrationState,
  meeting,
  meetingCode,
  memberStatus,
  interestAt,
  memberPreferences,
  profilePhone,
  user,
  userEmail,
  workshopEngagedAt,
  onJoinInterestList,
  onConfirmAttendance,
  onLeaveWorkshop,
  onSavePreferences,
  onSavePhone,
  confirming = false,
  leaving = false,
  meetingCodeMissing,
}) => {
  const phase = getWorkshopTabPhase(registrationState, meeting);
  const [prefsExpanded, setPrefsExpanded] = useState(false);

  const [rosterCount, setRosterCount] = useState<number | null>(null);

  useEffect(() => {
    if (phase === 'pre_start' || phase === 'running') {
      setPrefsExpanded(false);
    }
  }, [phase]);

  useEffect(() => {
    if (phase === 'signup') {
      setRosterCount(null);
    }
  }, [phase]);

  const leaveSection =
    onLeaveWorkshop && (registrationState === 'interest' || registrationState === 'confirmed') ? (
      <WorkshopLeaveSection
        registrationState={registrationState}
        onLeave={onLeaveWorkshop}
        leaving={leaving}
      />
    ) : null;

  const preferencesPanel = (
    <WorkshopPreferencesPanel
      meeting={meeting}
      preferences={memberPreferences}
      profilePhone={profilePhone}
      userEmail={userEmail}
      onSave={onSavePreferences}
      onSavePhone={onSavePhone}
      expanded={prefsExpanded}
      onExpandedChange={setPrefsExpanded}
    />
  );

  const stepWorkDiscoveryBanner =
    user && workshopEngagedAt && (registrationState === 'interest' || registrationState === 'confirmed') ? (
      <StepWorkDiscoveryBanner
        user={user}
        dismissKeySuffix="-workshop2026"
        mode="workshop"
        workshopEngagedAt={workshopEngagedAt}
      />
    ) : null;

  const participantsTitle =
    rosterCount != null ? `Workshop Participants (${rosterCount})` : 'Workshop Participants';

  const participantsCard =
    meetingCode && memberStatus ? (
      <WorkshopCollapsibleCard
        id="workshop-participants"
        title={participantsTitle}
        defaultOpen={false}
      >
        <WorkshopCommunitySection
          meetingCode={meetingCode}
          meeting={meeting}
          showPhoneList={phase === 'running'}
          onRosterCountChange={setRosterCount}
        />
        {phase === 'running' && (
          <>
            <h4 className={styles.workshopSubheading}>Phone list</h4>
            <WorkshopPhoneListSection
              meetingCode={meetingCode}
              meeting={meeting}
              memberStatus={memberStatus}
              onExpandPreferences={() => setPrefsExpanded(true)}
            />
          </>
        )}
      </WorkshopCollapsibleCard>
    ) : null;

  if (meetingCodeMissing) {
    return (
      <p className={styles.helperText}>
        Workshop registration is not available yet — an admin needs to link this page to a workshop
        record.
      </p>
    );
  }

  if (registrationState === 'none') {
    return (
      <>
        <h2 className={styles.learnHeading}>Join the 2026 workshop</h2>
        <p>
          Join the interest list to get updates about the start date by email. When you&apos;re ready
          to commit, you&apos;ll confirm you&apos;re planning to attend — then the Zoom link appears
          here.
        </p>
        <button type="button" className={styles.primaryBtn} onClick={onJoinInterestList}>
          Join interest list
        </button>
      </>
    );
  }

  if (phase === 'signup') {
    const dateLabel = formatInterestDate(interestAt);
    return (
      <div className={styles.workshopTabFlow}>
        {preferencesPanel}

        <section className={styles.workshopCard}>
          <h3 className={styles.workshopCardTitleStatic}>Your next step</h3>
          <ul className={`${styles.statusChecklist} ${styles.statusChecklistInCard}`}>
            <li className={styles.statusDone}>
              On interest list{dateLabel ? ` since ${dateLabel}` : ''}
            </li>
            <li className={styles.statusPending}>
              Confirm you&apos;re planning to attend — before first session
            </li>
            <li className={styles.statusPending}>Zoom link — available after you confirm</li>
          </ul>
          {onConfirmAttendance && (
            <div className={styles.workshopCardActions}>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={onConfirmAttendance}
                disabled={confirming}
              >
                {confirming ? 'Confirming…' : "I'm planning to attend"}
              </button>
            </div>
          )}
        </section>

        <WorkshopZoomBlock meeting={meeting} locked sectionId="join-live" />

        {stepWorkDiscoveryBanner}
        {leaveSection}
      </div>
    );
  }

  const stepWorkSection =
    meetingCode ? (
      <WorkshopStepWorkSection
        meetingCode={meetingCode}
        canFacilitate={!!memberStatus?.is_admin}
      />
    ) : null;

  if (phase === 'pre_start') {
    return (
      <div className={styles.workshopTabFlow}>
        <WorkshopZoomBlock
          meeting={meeting}
          zoomUrl={meeting?.zoom_url}
          sectionId="join-live"
        />

        {stepWorkSection}

        {participantsCard}

        <WorkshopCollapsibleCard id="share-invite" title="Share & invite" defaultOpen>
          <WorkshopShareInviteBlock />
        </WorkshopCollapsibleCard>

        <WorkshopCollapsibleCard id="workshop-calendar" title="Workshop calendar">
          <WorkshopCalendarMemberView meeting={meeting} />
        </WorkshopCollapsibleCard>

        {preferencesPanel}

        {stepWorkDiscoveryBanner}
        {leaveSection}
      </div>
    );
  }

  return (
    <div className={styles.workshopTabFlow}>
      <WorkshopZoomBlock
        meeting={meeting}
        zoomUrl={meeting?.zoom_url}
        sectionId="join-live"
      />

      {stepWorkSection}

      {participantsCard}

      <WorkshopCollapsibleCard id="workshop-calendar" title="Workshop calendar">
        <WorkshopCalendarMemberView meeting={meeting} />
      </WorkshopCollapsibleCard>

      <WorkshopCollapsibleCard id="share-invite" title="Share & invite">
        <WorkshopShareInviteBlock />
      </WorkshopCollapsibleCard>

      {preferencesPanel}

      {stepWorkDiscoveryBanner}
      {leaveSection}
    </div>
  );
};

export default Workshop2026WorkshopTab;
